import { UserInfo } from '@/types/userInfo';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ensureUserInfo } from '@/utils/userInfo';
import Loading from '@/components/Loading';
import { getRefCookie, clearRefCookie } from '@/utils/referral';
import { apiPostRequest } from '@/utils/apiClient';
import i18n from '@/i18n';
import { setTimeZoneProvider } from '@/i18n/dateFormats';
import { apiPutRequest } from '@/utils/apiClient';
import { resolveSupportedLanguage } from '@/utils/locale';

// Contextの型
type UserContextType = {
    userInfo: UserInfo | null;
    setUserInfo: (userInfo: UserInfo | null) => void;
    isLoading: boolean;
    refreshUserInfo: () => Promise<void>;
};

// 初期値
const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // ユーザー情報を取得する関数（初回ロード用。userInfo はマウント時は null）
    const fetchUserInfo = async () => {
        try {
            setIsLoading(true);
            await ensureUserInfo(userInfo, setUserInfo);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * 常に API から取り直す。setUserInfo(null) + fetchUserInfo だと、クロージャに残った
     * 古い userInfo で ensureUserInfo が早期 return し、null のまま固まることがある。
     * 全画面 Loading にしない（オンボーディングが一瞬消えるのを防ぐ）。
     */
    const refreshUserInfo = async () => {
        try {
            await ensureUserInfo(null, setUserInfo);
        } catch (error) {
            // 握りつぶさないとサイレント失敗しうる
            console.error('refreshUserInfo failed', error);
        }
    };

    // 初期化時にユーザー情報を取得
    useEffect(() => {
        fetchUserInfo();
    }, []);

    useEffect(() => {
        if (!userInfo) return;
        const targetLanguage = resolveSupportedLanguage(userInfo.language);
        if (i18n.language !== targetLanguage) {
            void i18n.changeLanguage(targetLanguage);
        }
        // フロント表示のTZをUserContext優先に
        setTimeZoneProvider(() => userInfo.timeZone);
    }, [userInfo]);

    // 認証済みになったら紹介特典を自動適用
    useEffect(() => {
        const applyReferralIfExists = async () => {
            // 未ログイン or ユーザー情報が未取得の間は何もしない
            if (!userInfo) return;
            const ref = getRefCookie();
            if (!ref) return;
            if (userInfo.isNewlyRegistered) {
                try {
                    const response = await apiPostRequest('/api/user/referral/register', {
                        code: ref,
                    });
                    // 4xx/5xx でもモーダルは出さない（クッキーは finally で削除）
                    if (!response.ok) {
                        return;
                    }
                } catch {
                    // ネットワークエラーなど: モーダルは出さない
                    return;
                } finally {
                    clearRefCookie();
                }
            } else {
                clearRefCookie();
            }
        };
        applyReferralIfExists();
    }, [userInfo]);

    // 初回のみ: ユーザーのTZが未設定/UTCなら、ブラウザTZを自動登録
    useEffect(() => {
        const registerInitialTimeZone = async () => {
            if (!userInfo) return;
            const tz = userInfo.timeZone;
            if (tz && tz !== 'UTC') return;
            let browserTz: string | undefined;
            try {
                browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch {}
            if (!browserTz) return;
            try {
                await apiPutRequest('/api/user/time-zone', { timeZone: browserTz });
                // 反映
                await refreshUserInfo();
            } catch {}
        };
        registerInitialTimeZone();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.timeZone]);

    // ローディング中は子コンポーネントを表示せず、ローディング画面を表示
    if (isLoading) {
        return <Loading />;
    }

    return (
        <UserContext.Provider
            value={{
                userInfo,
                setUserInfo,
                isLoading,
                refreshUserInfo,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

// 利用するための専用フック
export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
